import { CalendarClock, Pencil, Target } from "lucide-react";
import { Card } from "../ui/Card";
import { cn } from "../../lib/cn";
import { kgToDisplay } from "../../onboarding/types";
import { goalTypeLabel, type PlanProfile } from "../../screens/plan-utils";
import { formatGoalDateRange, formatWeeklyChange, type JourneySummary } from "../progress/utils";

const EDIT_BUTTON = cn(
  "flex min-h-[44px] items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2",
  "text-xs font-semibold text-accent select-none transition-all duration-200 ease-out active:scale-[0.98]",
);

function formatWeight(kg: number | undefined, unit: "kg" | "lbs"): string {
  if (kg === undefined) return "—";
  const display = kgToDisplay(kg, unit);
  return `${Number.isInteger(display) ? display : display.toFixed(1)} ${unit}`;
}

export interface GoalJourneyHeroProps {
  readonly profile: PlanProfile;
  readonly journey: JourneySummary;
  readonly onEditGoal: () => void;
}

function formatJourneyDate(ms: number): string {
  const parts = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" }).formatToParts(ms);
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  return `${day} ${month}`;
}

function statusCopy(journey: JourneySummary): string {
  if (journey.weighInDayCount === 0) return "Log your first weigh-in";
  if (journey.weighInDayCount === 1) return "Based on 1 weigh-in · trend not available";
  if (journey.weighInDayCount < 7) return "Early progress · your weight trend is still forming";
  return "Based on your weight trend";
}

export function GoalJourneyHero({ profile, journey, onEditGoal }: GoalJourneyHeroProps) {
  const unit = profile.unitWeight === "lbs" ? "lbs" : "kg";
  const currentWeightKg = journey.progressBasis === "trend"
    ? journey.currentTrendKg ?? journey.latestScale?.weightKg ?? journey.anchor.startKg
    : journey.latestScale?.weightKg ?? journey.anchor.startKg;
  const dateRange = formatGoalDateRange(journey.goalDateMin, journey.goalDateMax);
  const percent = journey.percentClamped ?? 0;
  const badgeLeft = Math.min(88, Math.max(12, percent));
  const changeMagnitude = Math.abs(journey.changeFromStartKg);
  const changeHeadline = changeMagnitude < 0.05
    ? `No change since ${formatJourneyDate(journey.anchor.startDateMs)}`
    : `${formatWeight(changeMagnitude, unit)} ${journey.changeFromStartKg < 0 ? "down" : "up"} since ${formatJourneyDate(journey.anchor.startDateMs)}`;
  const reachedGoal = journey.progressFractionUnclamped !== null && journey.progressFractionUnclamped >= 1;
  const aboveStart = journey.direction === "lose" && journey.changeFromStartKg > 0;
  const belowStart = journey.direction === "gain" && journey.changeFromStartKg < 0;

  return (
    <Card className="flex flex-col gap-4 border-accent/25 bg-accent-light/50 p-4" data-testid="plan-goal-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-surface p-2 text-accent">
            <Target size={18} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-text">Your journey</h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span
                className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-semibold text-accent"
                data-testid="goal-type"
              >
                {goalTypeLabel(profile.goalType)}
              </span>
              <span
                className="rounded-full bg-surface px-2 py-0.5 text-[10px] font-semibold text-text-muted"
                data-testid="goal-weekly-rate"
              >
                {formatWeeklyChange(journey.plannedRateKgPerWeek)}
              </span>
            </div>
          </div>
        </div>
        <button type="button" className={EDIT_BUTTON} data-testid="edit-goal-btn" onClick={onEditGoal}>
          <Pencil size={14} strokeWidth={2} />
          Edit goal
        </button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xl font-bold leading-tight text-text" data-testid="goal-current-weight">
            {changeHeadline}
          </p>
          <p className="mt-1.5 text-sm font-medium text-text-muted" data-testid="goal-remaining">
            {journey.direction === "maintain"
              ? "Your goal is to maintain this weight"
              : reachedGoal
                ? "Goal reached"
                : aboveStart
                  ? `${formatWeight(changeMagnitude, unit)} above your starting weight`
                  : belowStart
                    ? `${formatWeight(changeMagnitude, unit)} below your starting weight`
                    : journey.targetKg === null
                      ? "Set a goal weight to track progress"
                      : (
                        <>
                          <span>{formatWeight(journey.remainingKg ?? undefined, unit)} to goal</span>
                          {" · "}
                          <span className="font-semibold text-accent" data-testid="journey-percent">
                            {percent}% to {formatWeight(journey.targetKg, unit)}
                          </span>
                        </>
                      )}
          </p>
          {journey.direction === "maintain" && <span className="sr-only" data-testid="journey-percent">Maintenance goal</span>}
        </div>
        {(dateRange !== null || journey.plannedRateKgPerWeek !== null) && (
          <span
            className="flex items-center gap-1.5 rounded-full bg-surface px-2.5 py-1 text-[11px] font-semibold text-accent-dark"
            data-testid="goal-date-chip"
          >
            <CalendarClock size={12} strokeWidth={2} aria-hidden />
            {dateRange ?? `Planned pace: ${Math.abs(journey.plannedRateKgPerWeek ?? 0).toFixed(1)} kg/week`}
          </span>
        )}
      </div>

      {journey.direction !== null && journey.targetKg !== null && (
        <div className="pt-7" data-testid="journey-rail">
          <div className="relative">
            <div
              className="plan-marker-in absolute -top-7 z-10"
              style={{ left: `${badgeLeft}%`, transform: "translateX(-50%)" }}
              data-testid="journey-marker"
            >
              <span className="whitespace-nowrap rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
                 {formatWeight(currentWeightKg, unit)}
              </span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-surface">
              <div
                className="plan-fill-grow h-full rounded-full bg-accent"
                 style={{ width: `${percent}%` }}
                role="progressbar"
                 aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid="journey-fill"
              />
            </div>
            <div
              className="absolute -top-1 h-5 w-1 rounded-full bg-accent-dark"
               style={{ left: `calc(${percent}% - 2px)` }}
              aria-hidden
            />
          </div>
          <div className="mt-1.5 flex items-start justify-between gap-3 text-[11px] leading-tight text-text-muted">
            <div>
              <p className="font-semibold text-text">{formatWeight(journey.anchor.startKg, unit)}</p>
              <p>Start · {formatJourneyDate(journey.anchor.startDateMs)}</p>
            </div>
            <div className="text-center">
              <p className="font-semibold text-text">{formatWeight(currentWeightKg, unit)}</p>
              <p>Current</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-text" data-testid="goal-target-weight">
                 {formatWeight(journey.targetKg, unit)}
              </p>
              <p>Goal</p>
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-text-muted" data-testid="journey-status">{statusCopy(journey)}</p>
      {journey.trendMature && journey.latestScale !== null && (
        <p className="text-xs text-text-muted">Latest scale: {formatWeight(journey.latestScale.weightKg, unit)}</p>
      )}
    </Card>
  );
}
